import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { Main, MainTop, MainTopTitle, MainBody, WhiteBox, ButtonSection } from '@/components/Layout'
import { TabNavItems, TabContent, TabPane } from '@/components/tabs'
import { ValidationBoundary, ValidationState, Validator } from '@/components/validation'
import { Alert, FormGroup, Label, Row, Button as RsButton } from 'reactstrap'
import { DatePicker } from '@/components/DatePicker'
import CheckBox from '@/components/CheckBox'
import Resx from '@/components/Resx'
import LoadingIndicator from '@/components/LoadingIndicator'
import context from '@/context'
import { allowErrorDialog } from '@/utils/fetch'
import { useLocalization } from '@/hooks/localization'
import { RichTextEditor } from '@/components/richTextEditor'
import { BsrsGrid, Column } from '@/components/BsrsGrid'
import { DropDownList } from '@/components/dropDownList'
import { TdActions } from '@/components/tdActions'
import { Button } from '@/components/button'
import { AnnouncementMemberType } from '@/enums/AnnouncementMemberType'
import { actionCreators as announcementsActionCreators } from '@/store/announcements'
import { actionCreators as announcementsRecipientsActionCreators } from '@/store/AnnouncementsRecipients'
import { useRfc13Announcements } from '@/hooks/useFeatureFlags'

const institutionActionTypes = {
  INCLUDE: 'include',
  EXCLUDE: 'exclude',
  REMOVE: 'remove'
}

const toValidDate = (value) => {
  if (!value) {
    return null
  }

  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const getInstitutionActionType = (dataItem) => {
  if (dataItem.type === AnnouncementMemberType.EXCLUDE) {
    return institutionActionTypes.INCLUDE
  }
  if (dataItem.isDirectSelection) {
    return institutionActionTypes.REMOVE
  }
  if (dataItem.isIncludedByGroup) {
    return institutionActionTypes.EXCLUDE
  }
  return null
}

/**
 * InstitutionActionsCell component
 *
 * @param {function} onAction - function to call when the action button is clicked
 *
 * Used to render the actions for an institution in the announcement edit page
 */
const InstitutionActionsCell = ({ disabled, onAction }) => {
  return function InstitutionActionsCellImpl (props) {
    const { localize } = useLocalization()
    const item = props.dataItem
    const actionType = getInstitutionActionType(item)

    const confirmProps = actionType === institutionActionTypes.INCLUDE
      ? {
        title: 'Reporting.ReincludeInstitutionTitle',
        content: 'Reporting.ReincludeInstitutionContent'
      }
      : actionType === institutionActionTypes.EXCLUDE
        ? {
          title: 'Reporting.ExcludeInstitutionTitle',
          content: 'Reporting.ExcludeInstitutionContent'
        }
        : {
          title: 'Reporting.RemoveInstitutionTitle',
          content: 'Reporting.RemoveInstitutionContent'
        }

    return (
      <TdActions>
        { !disabled && <Button
          iconOnly
          icon={actionType === institutionActionTypes.INCLUDE ? 'plus' : 'x'}
          tooltip={localize(confirmProps.title)}
          tooltipPlacement='right'
          confirmTitle={localize(confirmProps.title)}
          confirmText={localize(confirmProps.content)}
          onClick={() => onAction(item)} />}
      </TdActions>
    )
  }
}

/**
 * InstitutionGroupActionsCell component
 *
 * @param {boolean} disabled - whether the component is disabled
 * @param {function} onRemove - function to call when the remove button is clicked
 * @return {JSX.Element} - the component
 *
 * Used to render the actions for an institution group in the announcement edit page
 */
const InstitutionGroupActionsCell = ({ disabled, onRemove }) => {
  return function InstitutionGroupActionsCellImpl (props) {
    return (
      <TdActions>
        {!disabled && <Button
          iconOnly
          icon='x'
          confirmTitle='Reporting.RemoveInstitutionGroupTitle'
          confirmText='Reporting.RemoveInstitutionGroupContent'
          onClick={() => onRemove(props.dataItem)} />}
      </TdActions>
    )
  }
}

const AnnouncementDetail = ({
  match,
  announcementsRecipients,
  saveAnnouncement: saveAnnouncementAction,
  saveInstitutionRecipient: saveInstitutionRecipientAction,
  excludeInstitutionRecipient: excludeInstitutionRecipientAction,
  removeInstitutionRecipient: removeInstitutionRecipientAction,
  saveInstitutionGroupRecipient: saveInstitutionGroupRecipientAction,
  removeInstitutionGroupRecipient: removeInstitutionGroupRecipientAction
}) => {
  const announcementId = parseInt(match.params.id, 10)
  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [saving, setSaving] = useState(false)
  const [announcement, setAnnouncement] = useState(null)
  const [recipientsState, setRecipientsState] = useState(null)
  const [activeTab, setActiveTab] = useState('1')
  const [selectedInstitution, setSelectedInstitution] = useState(null)
  const [selectedInstitutionGroup, setSelectedInstitutionGroup] = useState(null)
  const [nowTick, setNowTick] = useState(() => Date.now())
  const lastRecipientsReloadedAtRef = useRef(announcementsRecipients.announcementRecipientsLastReloadedAt)
  const isRfc13AnnouncementsEnabled = useRfc13Announcements()

  const { localize } = useLocalization()

  const showExternalRecipientsTab = !!(
    isRfc13AnnouncementsEnabled &&
    announcement &&
    announcement.isForExternalApp &&
    announcement.isOnlyForSelectedInstitutions
  )

  const hasAnyTarget = useMemo(() => {
    return announcement && (announcement.isForInternalApp || announcement.isForExternalApp)
  }, [announcement])

  const activePeriodState = useMemo(() => {
    if (!announcement || !announcement.isActive) {
      return { isExpired: false, isNotStartedYet: false }
    }

    const now = new Date(nowTick)
    const validFrom = toValidDate(announcement.validFrom)
    const validTo = toValidDate(announcement.validTo)

    return {
      isExpired: Boolean(validTo && now > validTo),
      isNotStartedYet: Boolean(validFrom && now < validFrom)
    }
  }, [announcement, nowTick])

  const canManageRecipients = showExternalRecipientsTab && !announcementsRecipients.changing

  const loadRecipientsState = useCallback(() => {
    return context.api.announcementService.getRecipientsState(announcementId, allowErrorDialog)
      .then(response => {
        setRecipientsState(response)
      })
      .catch(() => {
        setRecipientsState(null)
      })
  }, [announcementId])

  const reload = useCallback(() => {
    setLoading(true)
    setLoadFailed(false)
    Promise.all([
      context.api.announcementService.get(announcementId, allowErrorDialog),
      context.api.announcementService.getRecipientsState(announcementId, allowErrorDialog)
    ])
      .then(([announcementResponse, recipientsStateResponse]) => {
        setAnnouncement(announcementResponse)
        setRecipientsState(recipientsStateResponse)
        setLoading(false)
      })
      .catch(() => {
        setAnnouncement(null)
        setRecipientsState(null)
        setLoadFailed(true)
        setLoading(false)
      })
  }, [announcementId])

  useEffect(() => {
    reload()
  }, [reload])

  useEffect(() => {
    if (!showExternalRecipientsTab && activeTab === '2') {
      setActiveTab('1')
    }
  }, [showExternalRecipientsTab, activeTab])

  useEffect(() => {
    if (!announcement) {
      return
    }

    const lastReloadedAt = announcementsRecipients.announcementRecipientsLastReloadedAt
    if (lastRecipientsReloadedAtRef.current === lastReloadedAt) {
      return
    }

    lastRecipientsReloadedAtRef.current = lastReloadedAt
    loadRecipientsState()
  }, [announcement, loadRecipientsState, announcementsRecipients.announcementRecipientsLastReloadedAt])

  useEffect(() => {
    const timerId = setInterval(() => {
      setNowTick(Date.now())
    }, 60000)

    return () => clearInterval(timerId)
  }, [])

  const saveAnnouncement = useCallback(() => {
    if (!announcement) return
    setSaving(true)
    const itemToSave = {
      ...announcement,
      isOnlyForSelectedInstitutions: isRfc13AnnouncementsEnabled ? announcement.isOnlyForSelectedInstitutions : false
    }

    return saveAnnouncementAction(itemToSave)
      .then(() => reload())
      .catch(() => {
      })
      .finally(() => {
        setSaving(false)
      })
  }, [announcement, saveAnnouncementAction, reload, isRfc13AnnouncementsEnabled])

  const saveInstitutionRecipient = useCallback((institutionId, type = AnnouncementMemberType.MEMBER) => {
    saveInstitutionRecipientAction({
      announcementId,
      id: institutionId,
      type
    })
  }, [announcementId, saveInstitutionRecipientAction])

  const addInstitutionGroupRecipient = useCallback(() => {
    if (!selectedInstitutionGroup) return

    saveInstitutionGroupRecipientAction({
      announcementId,
      id: selectedInstitutionGroup.id
    })
    setSelectedInstitutionGroup(null)
  }, [selectedInstitutionGroup, saveInstitutionGroupRecipientAction, announcementId])

  const onInstitutionAction = useCallback((dataItem) => {
    if (!canManageRecipients) {
      return
    }
    const actionType = getInstitutionActionType(dataItem)
    if (actionType === institutionActionTypes.INCLUDE) {
      saveInstitutionRecipientAction({
        announcementId,
        id: dataItem.institutionId,
        type: AnnouncementMemberType.MEMBER
      })
      return
    }
    if (actionType === institutionActionTypes.EXCLUDE) {
      excludeInstitutionRecipientAction({
        announcementId,
        id: dataItem.institutionId
      })
      return
    }
    if (actionType === institutionActionTypes.REMOVE) {
      removeInstitutionRecipientAction(announcementId, dataItem.institutionId)
    }
  }, [
    canManageRecipients,
    saveInstitutionRecipientAction,
    excludeInstitutionRecipientAction,
    removeInstitutionRecipientAction,
    announcementId
  ])

  const reportSubmittersInstitutionsRowRender = useCallback((trElement, props) => {
    const rowProps = {}
    if (props.dataItem.type === AnnouncementMemberType.EXCLUDE) {
      rowProps.style = { color: 'rgb(208, 208, 208)' }
    }
    return React.cloneElement(trElement, { ...rowProps }, trElement.props.children)
  }, [])

  const institutionActionsCell = useMemo(() => InstitutionActionsCell({
    disabled: !canManageRecipients,
    onAction: onInstitutionAction
  }), [canManageRecipients, onInstitutionAction])

  const onInstitutionGroupRemove = useCallback((data) => {
    removeInstitutionGroupRecipientAction(announcementId, data.institutionGroupId)
  }, [announcementId, removeInstitutionGroupRecipientAction])

  const institutionGroupActionsCell = useMemo(() => InstitutionGroupActionsCell({
    disabled: !canManageRecipients,
    onRemove: onInstitutionGroupRemove
  }), [canManageRecipients, onInstitutionGroupRemove])

  const recipientInstitutionsDataSource = useCallback(
    request => context.api.announcementService.getAllRecipientInstitutionsTableData(announcementId, request),
    [announcementId]
  )

  const recipientInstitutionGroupsDataSource = useCallback(
    request => context.api.announcementService.getAllRecipientInstitutionGroupsTableData(announcementId, request),
    [announcementId]
  )

  if (loading) {
    return (
      <Main>
        <MainTop>
          <MainTopTitle backUrl='/announcements'>
            <Resx name='Announcements.AnnouncementSettings' />
          </MainTopTitle>
        </MainTop>
        <MainBody transparent>
          <LoadingIndicator />
        </MainBody>
      </Main>
    )
  }

  if (loadFailed || !announcement) {
    return (
      <Main>
        <MainTop>
          <MainTopTitle backUrl='/announcements'>
            <Resx name='Announcements.AnnouncementSettings' />
          </MainTopTitle>
        </MainTop>
        <MainBody transparent>
          <WhiteBox>
            <Alert color='danger'>
              <Resx name='ServerErrors.UnexpectedError' />
            </Alert>
            <ButtonSection>
              <RsButton color='secondary' onClick={reload}>
                {localize('Common.Reload')}
              </RsButton>
            </ButtonSection>
          </WhiteBox>
        </MainBody>
      </Main>
    )
  }

  return (
    <Main>
      <MainTop>
        <MainTopTitle backUrl='/announcements'>
          <Resx name='Announcements.AnnouncementSettings' />
        </MainTopTitle>
      </MainTop>
      <MainBody transparent>
        <WhiteBox>
          {(saving || loading) && <LoadingIndicator />}
          <TabNavItems
            activeTabId={activeTab}
            onClick={setActiveTab}
            items={[
              { id: '1', text: 'Announcements.AnnouncementSettings' },
              ...(showExternalRecipientsTab ? [{ id: '2', text: 'Announcements.ExternalRecipients' }] : [])
            ]} />
          <TabContent activeTab={activeTab}>
            <TabPane tabId='1'>
              <ValidationBoundary>
                <div>
                  <FormGroup>
                    <Label><Resx name='Common.ValidFrom' /></Label>
                    <Validator name='validFrom' value={announcement.validFrom} required>
                      <DatePicker
                        value={announcement.validFrom}
                        onChange={value => setAnnouncement(prev => ({ ...prev, validFrom: value }))} />
                    </Validator>
                  </FormGroup>
                  <FormGroup>
                    <Label><Resx name='Common.ValidTo' /></Label>
                    <Validator name='validTo' value={announcement.validTo} required>
                      <DatePicker
                        value={announcement.validTo}
                        onChange={value => setAnnouncement(prev => ({ ...prev, validTo: value }))} />
                    </Validator>
                  </FormGroup>
                  <FormGroup>
                    <CheckBox
                      label={localize('Common.Active')}
                      checked={announcement.isActive}
                      onChange={isActive => setAnnouncement(prev => ({ ...prev, isActive }))} />
                    {activePeriodState.isExpired && (
                      <Alert color='warning' className='mt-2'>
                        <Resx name='Announcements.ActiveAnnouncementExpiredWarning' />
                      </Alert>
                    )}
                    {activePeriodState.isNotStartedYet && (
                      <Alert color='warning' className='mt-2'>
                        <Resx name='Announcements.ActiveAnnouncementNotStartedWarning' />
                      </Alert>
                    )}
                  </FormGroup>
                  <FormGroup>
                    <Label><Resx name='Common.Text' /></Label>
                    <RichTextEditor
                      value={announcement.text}
                      onChange={text => setAnnouncement(prev => ({ ...prev, text }))} />
                  </FormGroup>
                  <FormGroup>
                    <Label><Resx name='Common.LatinText' /></Label>
                    <RichTextEditor
                      value={announcement.latinText}
                      onChange={latinText => setAnnouncement(prev => ({ ...prev, latinText }))} />
                  </FormGroup>
                  <FormGroup>
                    <CheckBox
                      label={localize('Enums.ApplicationTypes_0')}
                      checked={announcement.isForInternalApp}
                      onChange={isForInternalApp => setAnnouncement(prev => ({ ...prev, isForInternalApp }))} />
                    <CheckBox
                      label={localize('Enums.ApplicationTypes_1')}
                      checked={announcement.isForExternalApp}
                      onChange={isForExternalApp => setAnnouncement(prev => ({
                        ...prev,
                        isForExternalApp,
                        isOnlyForSelectedInstitutions: isForExternalApp ? prev.isOnlyForSelectedInstitutions : false
                      }))} />
                    {isRfc13AnnouncementsEnabled && (
                      <div className='ml-3 mt-1'>
                        <CheckBox
                          label={localize('Announcements.OnlySelectedExternalRecipients')}
                          checked={announcement.isOnlyForSelectedInstitutions}
                          disabled={!announcement.isForExternalApp}
                          onChange={isOnlyForSelectedInstitutions => setAnnouncement(prev => ({ ...prev, isOnlyForSelectedInstitutions }))} />
                        {announcement.isForExternalApp && announcement.isOnlyForSelectedInstitutions && recipientsState && !recipientsState.hasRecipients && (
                          <Alert color='warning' className='mt-2'>
                            <Resx name='Announcements.SelectedExternalRecipientsWarning' />
                          </Alert>
                        )}
                      </div>
                    )}
                  </FormGroup>
                </div>
                <ValidationState render={({ isValid }) => (
                  <ButtonSection>
                    <RsButton className='m-r-2' color='secondary' disabled={saving} onClick={reload}>
                      {localize('Common.Cancel')}
                    </RsButton>
                    <RsButton className='m-l-a' color='success' disabled={!isValid || !hasAnyTarget || saving} onClick={saveAnnouncement}>
                      {localize('Common.Save')}
                    </RsButton>
                  </ButtonSection>
                )} />
              </ValidationBoundary>
            </TabPane>
            <TabPane tabId='2'>
              <div className='box__title'>{localize('Common.Institutions')}</div>
              <BsrsGrid
                dataSource={recipientInstitutionsDataSource}
                lastReloadedAt={announcementsRecipients.announcementRecipientsLastReloadedAt}
                rowRender={reportSubmittersInstitutionsRowRender}
                filterable>
                <Column field='institutionName' title={localize('Common.InstitutionName')} editable={false} />
                <Column field='institutionLatinName' title={localize('Common.InstitutionLatinName')} editable={false} />
                <Column field='institutionGroupName' title={localize('Common.InstitutionGroupName')} editable={false} />
                <Column cell={institutionActionsCell} width='80px' filterable={false} sortable={false} />
              </BsrsGrid>

              <div className='AddNewReportSubmitter mt-sm-4'>
                <Row className='new-report-submitter'>
                  <div>
                    <DropDownList
                      dataSource={context.api.institutionService.getLookup}
                      placeholder={localize('Institutions.SelectInstitution')}
                      value={selectedInstitution}
                      onChange={({ item }) => setSelectedInstitution(item)}
                      disabled={!canManageRecipients} />
                  </div>
                  <div>
                    <RsButton
                      color='success'
                      disabled={!selectedInstitution || !canManageRecipients}
                      onClick={() => {
                        saveInstitutionRecipient(selectedInstitution.id)
                        setSelectedInstitution(null)
                      }}>
                      {localize('Common.Add')}
                    </RsButton>
                  </div>
                </Row>
              </div>

              <div className='box__title mt-sm-4'>{localize('Common.InstitutionGroups')}</div>
              <BsrsGrid
                dataSource={recipientInstitutionGroupsDataSource}
                lastReloadedAt={announcementsRecipients.announcementRecipientsLastReloadedAt}
                filterable>
                <Column field='institutionGroupName' title={localize('Common.InstitutionGroupName')} editable={false} />
                <Column field='institutionGroupLatinName' title={localize('Common.LatinName')} editable={false} />
                <Column cell={institutionGroupActionsCell} width='80px' filterable={false} sortable={false} />
              </BsrsGrid>

              <div className='AddNewReportSubmitter mt-sm-4'>
                <Row className='new-report-submitter'>
                  <div>
                    <DropDownList
                      dataSource={context.api.institutionGroupService.getLookup}
                      placeholder={localize('Institutions.SelectInstitutionGroup')}
                      value={selectedInstitutionGroup}
                      onChange={({ item }) => setSelectedInstitutionGroup(item)}
                      disabled={!canManageRecipients} />
                  </div>
                  <div>
                    <RsButton
                      color='success'
                      disabled={!selectedInstitutionGroup || !canManageRecipients}
                      onClick={addInstitutionGroupRecipient}>
                      {localize('Common.Add')}
                    </RsButton>
                  </div>
                </Row>
              </div>
            </TabPane>
          </TabContent>
        </WhiteBox>
      </MainBody>
    </Main>
  )
}

const mapStateToProps = (state, ownProps) => ({
  announcementsRecipients: state.announcementsRecipients,
  ...ownProps
})

const mapDispatchToProps = {
  saveAnnouncement: announcementsActionCreators.save,
  saveInstitutionRecipient: announcementsRecipientsActionCreators.saveInstitutionRecipient,
  excludeInstitutionRecipient: announcementsRecipientsActionCreators.excludeInstitutionRecipient,
  removeInstitutionRecipient: announcementsRecipientsActionCreators.removeInstitutionRecipient,
  saveInstitutionGroupRecipient: announcementsRecipientsActionCreators.saveInstitutionGroupRecipient,
  removeInstitutionGroupRecipient: announcementsRecipientsActionCreators.removeInstitutionGroupRecipient
}

export default connect(mapStateToProps, mapDispatchToProps)(AnnouncementDetail)



// WEBPACK FOOTER //
// ./src/internal-app/pages/administration/announcements/announcementDetail.js